import React, { useEffect, useCallback } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import IconButton from "@mui/material/IconButton";
import { Box, Tooltip, Typography } from "@mui/material";
import { FilterListOff, Visibility } from "@mui/icons-material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { colFilterDatePattern } from "_utils/colFilterDatePattern";

export const TableView = ({
  supplierData,
  rowSelection,
  setSelectedRows,
  setRowSelection,
  activeView,
  clearFilter,
}) => {
  const navigate = useNavigate();

  const handleViewSupplier = useCallback(
    (e, UserId) =>
      navigate(`/sd-view-supplier-info`, {
        state: { id: UserId },
      }),
    [],
  );

  const columns = [
    {
      accessorKey: "RegistrationDate",
      header: "Date of Registration",
      Cell: ({ row }) => {
        const rawDate = row.original.RegistrationDate;
        return dayjs(rawDate).format("MMMM D, YYYY");
      },
      filterFn: (row, id, filterValue) => {
        return colFilterDatePattern(row.original.RegistrationDate, filterValue);
      },
      sortingFn: (rowA, rowB, columnId) => {
        const dateA = dayjs(rowA.original.RegistrationDate);
        const dateB = dayjs(rowB.original.RegistrationDate);
        return dateA.isAfter(dateB) ? 1 : dateA.isBefore(dateB) ? -1 : 0;
      },
    },
    {
      accessorKey: "CompanyName",
      header: "Company Name",
      muiTableBodyCellProps: {
        align: "center",
        sx: { textAlign: "center !important" },
      },
    },
    { accessorKey: "ContactPerson", header: "Contact Person" },
    { accessorKey: "EmailAddress", header: "Email", size: 200 },
    { accessorKey: "PhoneNumber", header: "Phone Number" },
  ];

  const columnsExpiringIn = [
    { accessorKey: "CompanyName", header: "Company Name" },
    {
      accessorKey: "EmailAddress",
      header: "Email",
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId)?.toLowerCase() ?? "";
        const b = rowB.getValue(columnId)?.toLowerCase() ?? "";
        return a.localeCompare(b);
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: { textAlign: "center !important" },
      },
      size: 200,
    },
    {
      accessorKey: "RegistrationDate",
      header: "Date of Registration",
      Cell: ({ row }) => {
        const rawDate = row.original.RegistrationDate;
        return dayjs(rawDate).format("MMMM D, YYYY");
      },
      filterFn: (row, id, filterValue) => {
        return colFilterDatePattern(row.original.RegistrationDate, filterValue);
      },
      sortingFn: (rowA, rowB, columnId) => {
        const dateA = dayjs(rowA.original.RegistrationDate);
        const dateB = dayjs(rowB.original.RegistrationDate);
        return dateA.isAfter(dateB) ? 1 : dateA.isBefore(dateB) ? -1 : 0;
      },
    },
    {
      accessorKey: "ExpirationDate",
      header: "Expiration Date",
      Cell: ({ row }) => {
        const rawDate = row.original.ExpirationDate;
        return dayjs(rawDate).format("MMMM D, YYYY");
      },
      filterFn: (row, id, filterValue) => {
        return colFilterDatePattern(row.original.ExpirationDate, filterValue);
      },
      sortingFn: (rowA, rowB, columnId) => {
        const dateA = dayjs(rowA.original.ExpirationDate);
        const dateB = dayjs(rowB.original.ExpirationDate);
        return dateA.isAfter(dateB) ? 1 : dateA.isBefore(dateB) ? -1 : 0;
      },
    },
    {
      accessorKey: "DaysUntilExpiration",
      header: "Days until Expiration",
      Cell: ({ row }) => Number(row.original.DaysUntilExpiration),
      sortingFn: (rowA, rowB) => {
        const dateA = parseInt(rowA.original.DaysUntilExpiration, 10);
        const dateB = parseInt(rowB.original.DaysUntilExpiration, 10);
        return dateA - dateB;
      },
    },
  ];

  const displayColumn =
    activeView === "expiringIN" && activeView !== undefined
      ? columnsExpiringIn
      : columns;
  const hasExpirationDate = displayColumn.some(
    (col) => col.accessorKey === "ExpirationDate",
  );

  const hasDaysUntilExpiration = displayColumn.some(
    (col) => col.accessorKey === "DaysUntilExpiration",
  );

  const columnOrder =
    activeView === "expiringIN" && activeView !== undefined
      ? [
          "mrt-row-expand",
          "mrt-row-select",
          "CompanyName",
          "EmailAddress",
          "RegistrationDate",
          "ExpirationDate",
          "DaysUntilExpiration",
          "mrt-row-actions",
        ]
      : [
          "mrt-row-expand",
          "mrt-row-select",
          "RegistrationDate",
          "CompanyName",
          "ContactPerson",
          "EmailAddress",
          "PhoneNumber",
          "mrt-row-actions",
        ];
  const table = useMaterialReactTable({
    columns: displayColumn,
    data: supplierData && supplierData.length ? supplierData : [],
    layoutMode: "grid",
    enableHiding: false,
    columnFilterDisplayMode: "popover",
    enableFullScreenToggle: false,
    enableColumnActions: false,
    paginationDisplayMode: "pages",
    enableRowActions: true,
    enableRowSelection: false,
    enableExpandAll: false,
    positionExpandColumn: "first",
    positionActionsColumn: "last",
    positionToolbarAlertBanner: "none",
    autoResetPageIndex: false,
    enableColumnFilters: true,
    enableFilters: true,
    enableColumnFilterModes: false,
    enableToolbarInternalActions: false,
    state: {
      rowSelection,
      columnOrder,
    },
    muiTableHeadCellProps: {
      align: "center",
      sx: {
        textAlign: "center",
      },
    },
    muiTableBodyCellProps: {
      align: "center",
      sx: { textAlign: "center" },
    },
    getRowId: (row) => row.UserId, // Ensure unique IDs for rows
    onRowSelectionChange: (newRowSelection) => {
      setRowSelection(newRowSelection); // Update row selection state
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        header: "",
        size: 10, // make the expand column wider
        muiTableHeadCellProps: {
          sx: {
            display: "none", // Hide the expand column
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: "none", // Hide the expand column
          },
        },
      },
    },

    initialState: {
      columnOrder: [
        "mrt-row-expand",
        "mrt-row-select",
        "RegistrationDate",
        "CompanyName",
        "ContactPerson",
        "EmailAddress",
        "PhoneNumber",
        "ExpirationDate",
        "DaysUntilExpiration",
        "mrt-row-actions",
      ],
      sorting: [
        ...(hasExpirationDate ? [{ id: "ExpirationDate", desc: false }] : []),
        ...(hasDaysUntilExpiration
          ? [{ id: "DaysUntilExpiration", desc: false }]
          : []),
      ],
    },

    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Tooltip title="Clear All Filters" className="Deactivate">
          <div>
            <IconButton
              onClick={() => {
                table && table.resetColumnFilters();
                clearFilter();
              }}
            >
              <FilterListOff variant="contained" color="secondary" />
            </IconButton>
          </div>
        </Tooltip>
      </Box>
    ),
    renderRowActions: ({ row }) => {
      return (
        <div className="tableicons">
          {/* View Icon */}
          <IconButton
            className="view"
            onClick={(e) => handleViewSupplier(e, row.id)}
          >
            <Visibility color="primary" />
          </IconButton>
        </div>
      );
    },

    muiExpandButtonProps: {
      sx: {
        display: "none",
      },
    },
    renderEmptyRowsFallback: () => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 2,
          width: "100%",
        }}
      >
        <Typography sx={{fontStyle:'italic'}}>No results found for the provided search criteria.</Typography>
      </Box>
    ),
  });

  useEffect(() => {
    const selectedFlatRows = table.getSelectedRowModel().flatRows;
    setSelectedRows(selectedFlatRows.map((row) => row.original)); // Extract original row data
  }, [rowSelection, table]); // Re-run when rowSelection changes

  useEffect(() => {
    table.resetColumnFilters();
  }, [activeView]);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MaterialReactTable table={table} />
      </LocalizationProvider>
    </>
  );
};
